import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import styles from "./FeedbackModal.module.css";

const MAX_COMMENT_LENGTH = 250;

// Shared focusable-element selector used by BOTH the initial-focus query and the
// Tab focus-trap query so the two can never drift. Disabled controls are excluded
// (:not([disabled])) since they cannot receive focus.
//
// Note the clauses are independent: `button:not([disabled])` matches a button
// regardless of its tabindex, so the trailing [tabindex] clause does NOT subtract
// tabindex="-1" buttons from the set. Every control in this modal is either tabbable
// or disabled, so the two agree today — but do not add a tabindex="-1" button here
// and assume it is excluded.
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Mirrors the server's hasMeaningfulComment (handler_feedback.go): a comment that is
// nothing but whitespace and invisible formatting characters is not a comment. String
// .trim() only strips whitespace, so a lone U+200B ZERO WIDTH SPACE would pass a .trim()
// check, enable Submit, and then take a 400 — and that 400 tells the user to reload,
// which cannot help them, because they are looking straight at a comment box holding
// characters they cannot see. Any gap between these two rules is that dead end.
//
// The set is \p{Cf} rather than an enumeration, because enumerating it is what went
// wrong: the previous hand-written list covered only a subset of Cf and left 146 code
// points client-visible but server-invisible, among them the U+2066-U+2069 bidi isolates
// that ride along in text copied out of a browser or a chat client.
//
// U+0085 (NEL) stays listed explicitly: Go's unicode.IsSpace treats it as space, JS \s
// does not, and it is Cc rather than Cf — so \p{Cf} does not cover it, and dropping it
// would reopen the same one-way gap for one code point. U+00AD and U+180E mirror the two
// the server special-cases; both are Cf today, so they are belt-and-braces.
//
// Residual risk, accepted: the two rules are identical in intent but read from different
// Unicode tables (Go's unicode package vs. the JS engine's), so a newly assigned Cf code
// point can be known to one side before the other — a far smaller surface than a
// hand-maintained list, and one that stays inside the same single-code-point scale.
const INVISIBLE_ONLY = /^[\s\u0085\p{Cf}\u00ad\u180e]*$/u;

const hasMeaningfulComment = (value) => !INVISIBLE_ONLY.test(value);

/**
 * FeedbackModal - accessible, portal-rendered feedback modal for ModernUI.
 * Mirrors ConfirmationModal's a11y mechanics (createPortal, role="dialog",
 * aria-modal, focus trap, Escape-to-close, focus restore) but is its own
 * component: it captures a required comment capped at 250 characters with a
 * visible counter, plus an optional thumbs up/down rating.
 *
 * US2090203: the comment is the required field (the thumb used to be) and every
 * submission is attributed to the signed-in user by the backend, which reads the
 * identity from the session. There is no anonymous option, so the modal discloses
 * the attribution instead of offering a choice about it.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility.
 * @param {function} props.onClose - Called when the modal is cancelled/closed.
 * @param {function} props.onSubmit - Called with { thumb, comment } on submit.
 * @param {boolean} props.submitting - Disables controls while a submit is in flight.
 * @param {string|null} props.error - Error message to display, if any.
 */
const FeedbackModal = ({ isOpen, onClose, onSubmit, submitting = false, error = null }) => {
  const modalRef = useRef(null);
  const openerRef = useRef(null);
  const commentRef = useRef(null);
  const [thumb, setThumb] = useState(null);
  const [comment, setComment] = useState("");

  // US2090203: onClose is held in a ref so the focus effect below can depend on
  // `isOpen` ALONE. A caller that passes an inline arrow (a new identity every render)
  // would otherwise make that effect tear down and re-run on every parent render,
  // yanking focus out of the dialog and onto its Close button — which now destroys the
  // user's required comment draft. The ref keeps the modal correct regardless of what
  // the caller does.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Same reason as onCloseRef: the keydown handler below is created inside an effect with
  // deps [isOpen], so a direct `submitting` read there would be frozen at the value from
  // the open render — permanently false — and the Escape guard would never fire.
  const submittingRef = useRef(submitting);
  useEffect(() => {
    submittingRef.current = submitting;
  }, [submitting]);

  // Reset transient form state each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setThumb(null);
      setComment("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    openerRef.current = document.activeElement;

    const modalNode = modalRef.current;
    if (modalNode) {
      // US2090203: open onto the COMMENT FIELD, not focusables[0].
      //
      // focusables[0] is the close X, so opening used to put focus straight onto the
      // destructive control: Enter or Space immediately after opening discarded the
      // dialog, and reaching the field this story made *required* cost four tabs. The
      // comment is now the primary input, so it is what should receive focus — and a
      // textarea is a safe landing spot, where a stray keystroke types rather than
      // destroys. Falls back to the first focusable if the ref is somehow unset.
      const focusables = modalNode.querySelectorAll(FOCUSABLE_SELECTOR);
      const initial = commentRef.current || focusables[0];
      if (initial) {
        initial.focus();
      }
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        // Escape is a third way out of the dialog, alongside Cancel and the close X —
        // and both of those carry disabled={submitting} precisely so the modal cannot
        // unmount while the POST is in flight. Without the same guard here, Escape
        // during a submit unmounts the modal, and a subsequent failure has nowhere to
        // render: the user loses a now-MANDATORY comment and is never told the submit
        // failed. Read through the ref — see submittingRef above.
        if (submittingRef.current) return;
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !modalNode) return;

      const focusables = Array.from(modalNode.querySelectorAll(FOCUSABLE_SELECTOR));
      const active = document.activeElement;

      // The edge checks below only fire when focus is on a control INSIDE the dialog.
      // Focus can legitimately be elsewhere: disabling the focused element blurs it to
      // document.body (see the submitting effect below), and the dialog container itself
      // is focusable via tabIndex={-1} but is not in `focusables`. In both cases the
      // active element matches neither edge, so without this branch Tab fell through to
      // the browser default and walked straight out of the dialog into the page behind.
      if (!active || active === modalNode || !modalNode.contains(active)) {
        e.preventDefault();
        // The `|| modalNode` fallback is load-bearing, not defensive: during a submit
        // EVERY control including the close X is disabled, so `focusables` is empty and
        // there is nothing inside to move to. Hold focus on the container rather than
        // letting Tab leave the dialog.
        const target = e.shiftKey ? focusables[focusables.length - 1] : focusables[0];
        (target || modalNode).focus();
        return;
      }

      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const opener = openerRef.current;
      if (opener && typeof opener.focus === "function" && document.body.contains(opener)) {
        opener.focus();
      }
    };
    // Deliberately NOT depending on onClose — see onCloseRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // US2090203: keep focus inside the dialog across the submitting transition.
  //
  // Clicking Submit disables Submit, Cancel, both thumbs and the textarea in one render.
  // A browser blurs a focused element the moment it becomes disabled, so activeElement
  // becomes document.body — outside the dialog — and if the submit then fails, focus is
  // still nowhere when the error appears. Until this PR the focus effect above depended
  // on `onClose`, which ModernHeader passed as an inline arrow, so it happened to tear
  // down and re-run on the submitting render and re-focus its way back in. That effect
  // now depends on `isOpen` alone (deliberately — the re-runs destroyed the draft), so
  // the accident is gone and the recovery has to be explicit.
  //
  // Focus moves to the dialog container because there is nowhere else to put it: as of
  // the close X gaining disabled={submitting}, EVERY control is disabled during a submit,
  // so the container (tabIndex={-1}) is the only focusable node left. That is also the
  // right place on its own merits — parking focus on a destructive control while a
  // request is in flight invites a stray Enter that discards a submission already sent.
  useEffect(() => {
    if (!isOpen || !submitting) return;

    const modalNode = modalRef.current;
    if (!modalNode) return;

    // Already somewhere inside the dialog — leave focus alone. In practice a real browser
    // will have blurred to document.body by now, since as of the close X gaining
    // disabled={submitting} every control is disabled; this guard matters for the
    // container itself, because `contains` is true for the node it is called on, so a
    // second submitting render will not pointlessly re-focus it.
    const active = document.activeElement;
    if (active && modalNode.contains(active)) return;

    modalNode.focus();
  }, [isOpen, submitting]);

  if (!isOpen) return null;

  const hasDraft = hasMeaningfulComment(comment);

  const handleBackdropClick = (e) => {
    if (e.target !== e.currentTarget) return;
    // US2090203: the overlay covers the whole viewport while the card is only 500px
    // wide, so a stray click beside the modal is easy — and the comment is now the
    // required field, so silently discarding a draft is real data loss with no undo.
    // An accidental backdrop click is ignored while a draft exists; Cancel, the X, and
    // Escape still close, since those are unambiguous intent.
    if (hasDraft) return;
    onClose();
  };

  const handleSubmit = () => {
    // US2090203: the comment gates submission; thumb may be null (unrated).
    if (!hasDraft || submitting) return;
    onSubmit({ thumb, comment });
  };

  // US2090203: the thumbs are an OPTIONAL rating rendered as two independent toggle
  // buttons (aria-pressed), not an ARIA radiogroup. A radiogroup cannot express
  // "nothing selected" once a choice is made — ARIA radios cannot be unchecked by
  // re-activation — so the earlier radio markup left keyboard users with no route back
  // to unrated and made any arrow key silently commit a rating on an optional field.
  // Toggle buttons model it correctly: both are tabbable, Space/Enter toggles, and
  // pressing the active one returns to unrated.
  const handleThumbClick = (value) => {
    setThumb((current) => (current === value ? null : value));
  };

  const nearLimit = comment.length > MAX_COMMENT_LENGTH - 20;
  const charactersLeft = MAX_COMMENT_LENGTH - comment.length;
  // US2090203: Submit is gated on the comment, not the thumb. Trimmed so a
  // whitespace-only comment cannot be submitted — the server rejects it too.
  // Gated on hasDraft (not comment.trim()) so the button's disabled state uses the
  // SAME rule as handleSubmit and as the server — otherwise an invisible-only comment
  // enables Submit and the user gets a 400 for text they cannot see.
  const submitDisabled = !hasDraft || submitting;

  return createPortal(
    <div className={styles.overlay} onClick={handleBackdropClick}>
      <div
        className={styles.modal}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
        /* Programmatic focus target for the submitting effect above. tabIndex={-1} keeps
           it out of the Tab order, and querySelectorAll never returns the node it is
           called on, so this does NOT add the container to FOCUSABLE_SELECTOR's set —
           the Tab handler treats `active === modalNode` as its own case. */
        tabIndex={-1}
      >
        <div className={styles.header}>
          <h2 id="feedback-modal-title" className={styles.title}>
            Send Feedback
          </h2>
          {/* US2090203: disabled while submitting, like every other control — Cancel and
              the Escape handler carry the same guard. Without it the X could unmount the
              modal mid-request, and a subsequent failure then has nowhere to render: the
              user is never told, and has silently lost a comment that is now MANDATORY.
              (The hook itself is fine — useFeedbackSubmit lives in ModernHeader, which
              does not unmount, so `error` is still set; there is simply no longer a modal
              displaying it.) Disabling this leaves the focusable set empty during a
              submit, which the Tab handler and the submitting effect both handle by
              holding focus on the dialog container. */}
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.ratingGroup} role="group" aria-labelledby="feedback-rating-label">
            <span className={styles.label} id="feedback-rating-label">
              Rating (optional)
            </span>
            <div className={styles.thumbGroup}>
              <button
                type="button"
                className={`${styles.thumbButton} ${thumb === "up" ? styles.thumbSelected : ""}`}
                onClick={() => handleThumbClick("up")}
                aria-pressed={thumb === "up"}
                aria-label="Thumbs up"
                disabled={submitting}
              >
                <span aria-hidden="true">👍</span>
              </button>
              <button
                type="button"
                className={`${styles.thumbButton} ${thumb === "down" ? styles.thumbSelected : ""}`}
                onClick={() => handleThumbClick("down")}
                aria-pressed={thumb === "down"}
                aria-label="Thumbs down"
                disabled={submitting}
              >
                <span aria-hidden="true">👎</span>
              </button>
            </div>
          </div>

          <div className={styles.commentGroup}>
            <label className={styles.label} htmlFor="feedback-comment">
              Comment (required)
            </label>
            <textarea
              id="feedback-comment"
              ref={commentRef}
              className={styles.textarea}
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
              maxLength={MAX_COMMENT_LENGTH}
              rows={4}
              aria-required="true"
              aria-invalid={error ? "true" : undefined}
              /* The attribution disclosure is only reachable by a screen-reader user
                 through this description — in forms/focus mode a standalone <p> between
                 controls is skipped, and that disclosure is what the team accepted IN
                 PLACE OF an anonymity opt-out. The error is described here too so a
                 failed submit is tied to the field it concerns. */
              aria-describedby={
                error ? "feedback-error feedback-attribution" : "feedback-attribution"
              }
              disabled={submitting}
            />
            <div className={styles.counterRow}>
              {/* role="alert" matches the convention used across Modern (see
                  PastIncidentsFilterMenu, PagerDutyActivity, JoinBridgeButton): without
                  it a submit failure is never announced, and the counter's own live
                  region below is a SIBLING that cannot announce this. */}
              {error ? (
                <span id="feedback-error" className={styles.error} role="alert">
                  {error}
                </span>
              ) : (
                <span id="feedback-error" />
              )}
              {/* Non-color cue when near the limit (US1989116 AC#8): the text changes to
                  an explicit "N characters left". The live region is gated on nearLimit —
                  when it was unconditional, the "N/250" text mutated on every keystroke
                  and a screen reader announced every single one over the user's own
                  typing echo. The .warning color is a supplementary cue, not the only
                  signal.

                  ACCEPTED TRADE-OFF (raised in review, deliberately not fixed here):
                  flipping aria-live from "off" to "polite" in the same commit as the text
                  change is not guaranteed to announce that FIRST change — some assistive
                  tech registers live-region membership before the mutation it would
                  announce, so the 231st-character crossing may be silent. The robust
                  shape is a permanently-polite region that receives content only when
                  nearLimit.

                  Left as-is on purpose. The non-color cue AC#8 asks for is the visible
                  text, which is unaffected either way, and the announcement is polish on
                  top of it — US2090203 introduced this gating to stop keystroke spam, it
                  did not introduce a requirement to announce. Every clean fix also costs
                  more than it looks: a separate region duplicates "N characters left"
                  into two nodes, which makes 11 existing getByText assertions ambiguous,
                  and needs a visually-hidden utility this codebase does not have. Worth
                  doing as its own change with a screen-reader check, not blind. */}
              <span
                className={`${styles.characterCount} ${nearLimit ? styles.warning : ""}`}
                aria-live={nearLimit ? "polite" : "off"}
              >
                {nearLimit
                  ? `${charactersLeft} characters left`
                  : `${comment.length}/${MAX_COMMENT_LENGTH}`}
              </span>
            </div>
          </div>

          {/* US2090203: attribution is disclosed, not offered as a choice —
              feedback is always submitted with the signed-in user's identity
              (resolved server-side from the session) so the team can follow up.
              Referenced by the textarea's aria-describedby. */}
          <p id="feedback-attribution" className={styles.attribution}>
            Your name and email are included with your feedback so we can follow up.
          </p>
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={submitDisabled}
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

FeedbackModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  error: PropTypes.string,
};

export default FeedbackModal;
